/**
 * **How big the page is, and how big the world is drawn on it.**
 *
 * One rule holds this file together, and every touch bug it fixes came from
 * breaking it:
 *
 *   > the canvas's box on screen must be exactly the size the renderer thinks
 *   > it is, because a pointer arrives in the first space and is read in the
 *   > second.
 *
 * The client used to leave that to Pixi's `resizeTo: window`, which sizes the
 * *drawing buffer* from `window.innerHeight`, while the stylesheet sized the
 * *element* with `height: 100vh`. On a desktop the two numbers are equal and
 * nothing is wrong. On a phone they are **not**: `100vh` is deliberately the
 * height with the address bar hidden, and `innerHeight` is the height right
 * now — so with the bar on screen the world was drawn into a box about a tenth
 * shorter than the one it was stretched across, and every tap was read that
 * tenth too high, worsening down the screen. That is the whole of "it doesn't
 * register where I tap unless the game is full screen": the game was full
 * screen exactly when the two numbers agreed again.
 *
 * So nothing here computes what the box *ought* to be. It measures what the box
 * *is* — `getBoundingClientRect`, in the same coordinate space `clientX` and
 * `clientY` are quoted in — and tells the renderer to match. Whatever the
 * browser decides a viewport means this second, the two agree by construction.
 */

/**
 * The page's own width and height, for placing things in the document.
 *
 * `documentElement.clientWidth` rather than `window.innerWidth` because it is
 * the *layout* viewport: the space DOM coordinates live in, and the one number
 * a pinch-zoom does not move. Under a pinch, `innerWidth` shrinks to the part
 * of the page you can see — clamping a menu to that would jam it into the
 * corner you happened to be looking at.
 */
export const pageW = () => document.documentElement.clientWidth || window.innerWidth;
export const pageH = () => document.documentElement.clientHeight || window.innerHeight;

/** How often the canvas is re-measured when no event has announced a change. */
const RECHECK_MS = 250;

let target: HTMLCanvasElement | null = null;
let apply: ((w: number, h: number) => void) | null = null;
let nextCheck = 0;
let lastW = 0;
let lastH = 0;

/** The canvas box as last measured, in client coordinates. Dev handle. */
export const surface = { left: 0, top: 0, width: 0, height: 0 };

function measure() {
  if (!target || !apply) return;
  const r = target.getBoundingClientRect();
  surface.left = r.left;
  surface.top = r.top;
  surface.width = r.width;
  surface.height = r.height;
  const w = Math.max(1, Math.round(r.width));
  const h = Math.max(1, Math.round(r.height));
  if (w === lastW && h === lastH) return;
  lastW = w;
  lastH = h;
  apply(w, h);
}

/**
 * Keep `canvas` and the renderer the same size, for as long as the page lives.
 *
 * Events first, because they are free and immediate. The poll is the backstop:
 * a mobile address bar slides away without firing `resize` in every browser,
 * and a viewport that changed silently is precisely the case that puts taps in
 * the wrong place — it must not depend on being announced.
 */
export function trackViewport(canvas: HTMLCanvasElement, resize: (w: number, h: number) => void) {
  target = canvas;
  apply = resize;
  measure();
  window.addEventListener('resize', measure);
  window.addEventListener('orientationchange', measure);
  window.visualViewport?.addEventListener('resize', measure);
  window.visualViewport?.addEventListener('scroll', measure);
}

/** Called from the frame loop; re-measures at `RECHECK_MS`, not every frame. */
export function syncViewport(now: number) {
  if (now < nextCheck) return;
  nextCheck = now + RECHECK_MS;
  measure();
}
